import ArrowLink from '../components/ArrowLink.jsx';
import PageHero from '../components/PageHero.jsx';
import ResponsiveImage from '../components/ResponsiveImage.jsx';
import SectionHeader from '../components/SectionHeader.jsx';

const challenges = [
  ['Health', 'Rising health disparities and limited access to care and wellness resources.'],
  ['Education', 'Unequal access to quality education and opportunities to succeed.'],
  ['Community', 'Communities struggle with safety, connection, and essential support.'],
  ['Opportunity', 'Economic barriers limit potential and long-term self-sufficiency.'],
  ['Global impact', 'Sustainable change requires unified action across borders.'],
];

const differences = [
  ['Holistic approach', 'Addressing the whole person and every dimension of community well-being.'],
  ['Strong partnerships', 'Joining organizations, businesses, and leaders to multiply resources and expertise.'],
  ['Sustainable solutions', 'Investing in long-term change that creates measurable and lasting impact.'],
  ['Community empowerment', 'Building capacity and opportunities so communities thrive from within.'],
  ['Legacy in action', 'Creating a ripple effect of hope, healing, and prosperity for generations.'],
];

export default function OurStory() {
  return (
    <>
      <PageHero label="The K.E.E.N. Movement" title="Purpose becomes powerful when people move together." intro="A community-driven initiative uniting businesses, healthcare providers, educators, nonprofits, government agencies, and volunteers around shared purpose." dark />

      <section className="founder section-pad">
        <div className="shell founder__grid">
          <figure className="founder__portrait reveal">
            <ResponsiveImage
              src="/assets/chanra-hai-2026.jpg"
              alt="Chanra Hai, founder, chairwoman, and chief executive officer of K.E.E.N."
              sizes="(max-width: 900px) 100vw, 38vw"
            />
          </figure>
          <div className="founder__story reveal reveal--delay">
            <p className="overline">Founder biography</p>
            <h2>Chanra Hai</h2>
            <h3>Founder, Chairwoman & Chief Executive Officer, Khmer Educational Endowment Network (K.E.E.N.)</h3>
            <p>Chanra Hai is a transformational wellness advocate and founder of the Khmer Educational Endowment Network (K.E.E.N.), a nonprofit dedicated to preserving Khmer culture while advancing education, wellness, leadership, and community development.</p>
            <p>Born in Austin, Texas, and raised in Stockton, California, Chanra has devoted her life to service since the age of fifteen. Her professional background includes more than twenty years as a medical interpreter, along with experience as a licensed cosmetologist, massage therapist, and transformational wellness practitioner. Today, she manages a wellness center in San Francisco’s Union Square, where she promotes holistic healing through compassionate, whole-person care.</p>
            <p>K.E.E.N. was inspired by a promise Chanra made to her father during the final week of his life following a cardiac arrest and stroke. Together they envisioned a community rooted in compassion, education, wellness, cultural preservation, and service. That promise became the foundation of K.E.E.N. and continues to guide its mission today.</p>
            <p>Under her leadership, K.E.E.N. has grown into a movement dedicated to empowering communities through education, cultural preservation, wellness initiatives, leadership development, and humanitarian service.</p>
            <blockquote>“The greatest measure of success is not found in personal achievements, but in the lives we uplift, the opportunities we create, and the hope we leave for future generations.”<cite>Chanra Hai</cite></blockquote>
          </div>
        </div>
      </section>

      <section className="why-section section-pad">
        <div className="shell">
          <SectionHeader label="Why K.E.E.N. exists" title="The need is great. The time is now." body="Communities face complex challenges that impact health, education, opportunity, and hope. Systemic barriers, growing needs, limited resources, and fragmented efforts prevent people from thriving." />
          <div className="editorial-list">
            {challenges.map(([title, copy], index) => <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}
          </div>
          <div className="why-section__notes">
            <p><strong>Disparities persist.</strong> Too many individuals lack access to essential healthcare, education, and economic opportunity.</p>
            <p><strong>Communities need support.</strong> Local organizations are doing incredible work, but resources and collaboration are limited.</p>
            <p><strong>Global connections matter.</strong> Meaningful partnerships can bridge communities and drive sustainable impact worldwide.</p>
            <p><strong>A movement can multiply impact.</strong> When we unite around a shared purpose, we create change that lasts for generations.</p>
          </div>
        </div>
      </section>

      <section className="difference section-pad section-dark">
        <div className="shell">
          <SectionHeader light label="The K.E.E.N. difference" title="One movement. Seven pillars. Limitless impact." body="The K.E.E.N. Movement unites purpose, people, and partnerships to create sustainable solutions and transform lives - locally and globally." />
          <div className="difference__grid">
            {differences.map(([title, copy], index) => <article key={title}><span>{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}
          </div>
          <p className="difference__close">By combining compassion with collaboration, innovation with integrity, and vision with execution, the K.E.E.N. Movement is creating a future where every community can flourish.</p>
        </div>
      </section>

      <section className="section-pad centered-cta">
        <div className="shell narrow">
          <h2>Building legacies. Changing lives.</h2>
          <p>Our mission is to unite organizations around shared purpose, creating measurable impact while advancing local and global initiatives through Transformational Wellness San Francisco.</p>
          <ArrowLink href="/pillars">Explore the seven pillars</ArrowLink>
        </div>
      </section>
    </>
  );
}
